/** Pendant zu `Services/API/ApiViewService.cs`. */

import type {
  ChinaAppSysGroup,
  ChinaAppSysGroupView,
  ChinaAppSysView,
  ChinaAppSysViewField,
} from '@/types/entities';

import {
  buildAppPath,
  createCrudApi,
  nullResult,
  requestOrFallback,
  requestSucceeded,
  type CrudApi,
} from '../apiBase';
import { apiClient } from '../httpClient';

const ENDPOINT = 'views';

export const viewApi: CrudApi<ChinaAppSysView> & {
  getFields(id: number): Promise<ChinaAppSysViewField[]>;
  addField(id: number, field: ChinaAppSysViewField): Promise<boolean>;
  putField(id: number, field: ChinaAppSysViewField): Promise<ChinaAppSysViewField>;
  deleteField(id: number, fieldId: number): Promise<boolean>;
  getGroups(id: number): Promise<ChinaAppSysGroup[]>;
  addGroup(id: number, groupView: ChinaAppSysGroupView): Promise<boolean>;
  removeGroup(id: number, groupId: number): Promise<boolean>;
} = {
  ...createCrudApi<ChinaAppSysView>(ENDPOINT),

  getFields: (id) =>
    requestOrFallback(
      () => apiClient.get<ChinaAppSysViewField[]>(`${buildAppPath(ENDPOINT)}/${id}/fields`),
      [],
    ),

  // Der abschließende Schrägstrich stammt aus dem Altclient und bleibt erhalten.
  addField: (id, field) =>
    requestSucceeded(() =>
      apiClient.raw('POST', `${buildAppPath(ENDPOINT)}/${id}/fields/add/`, field),
    ),

  // Aktualisieren und Löschen laufen über den Endpunkt "groupviewfields"; die View-Id
  // wird dabei nicht verwendet.
  putField: (_id, field) =>
    requestOrFallback(
      () =>
        apiClient.put<ChinaAppSysViewField>(
          `${buildAppPath('groupviewfields')}/${field.Id}`,
          field,
        ),
      nullResult<ChinaAppSysViewField>(),
    ),

  deleteField: (_id, fieldId) =>
    requestSucceeded(() =>
      apiClient.raw('DELETE', `${buildAppPath('groupviewfields')}/${fieldId}`),
    ),

  // `GET views/{id}/groups` liefert die Gruppen selbst (`List<ChinaAppSysGroup>` in
  // `ViewsController.GetGroups`), nicht die Zuordnungen — `Views` und `Users` sind darin
  // ausgeräumt.
  getGroups: (id) =>
    requestOrFallback(
      () => apiClient.get<ChinaAppSysGroup[]>(`${buildAppPath(ENDPOINT)}/${id}/groups`),
      [],
    ),

  /**
   * Erwartet eine vollständige `ChinaAppSysGroupView`; `ViewId` muss zur Route passen,
   * sonst antwortet `AddGroupToView` mit 400. Die Rechte übernimmt die API unverändert —
   * sie belegt dort nichts vor.
   */
  addGroup: (id, groupView) =>
    requestSucceeded(() =>
      apiClient.raw('POST', `${buildAppPath(ENDPOINT)}/${id}/groups/add`, groupView),
    ),

  removeGroup: (id, groupId) =>
    requestSucceeded(() =>
      apiClient.raw('DELETE', `${buildAppPath(ENDPOINT)}/${id}/groups/remove/${groupId}`),
    ),
};
